import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/CTEP/images";

export default function CTEPPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const assemblyFrames = Array.from({ length: 25 }, (_, i) =>
    `${BASE}/Gif/Frame_${String(i).padStart(5, "0")}.png`
  );
  const assemblyCaps = assemblyFrames.map((_, i) => `Frame ${String(i + 1).padStart(2, "0")}`);

  const inputs = [
    `${BASE}/in11.png`,
    `${BASE}/in12.png`,
    `${BASE}/in13.png`,
    `${BASE}/in14.png`,
  ];
  const outputs = [
    `${BASE}/out11.png`,
    `${BASE}/out12.png`,
    `${BASE}/out13.png`,
    `${BASE}/out14.png`,
  ];
  const inOutCaps = ["Configuration 01", "Configuration 02", "Configuration 03", "Configuration 04"];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="CTEP"
          description="A modular robotic system that uses electropermanent magnets as reconfigurable joints — turning a flat field of cells into volumetric assemblies that can be reshaped on demand. CTEP explores robotics, modular design, and tangible interfaces in one prototype."
          meta={["Research prototype", "Electropermanent magnets", "Rhino", "Custom firmware", "2021"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/ViewCapture20211227_131540.png`}
                alt="CTEP — main system view"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="System overview">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "CTEP treats every cell of an assembly as an independent switchable magnet: state-on, state-off, set by a brief current pulse and held without power. Combined into a modular grid, the cells become a tangible interface — the same physical hardware can be reconfigured into different volumetric shapes by selectively bonding or releasing neighbours.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={assemblyFrames}
                  captions={assemblyCaps}
                  label="Assembly sequence"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Inputs and outputs">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "A controller defines a target configuration for the field. The system reads the input geometry, computes the activation pattern, and pulses the magnets so that the assembly snaps into the requested shape — a one-step physical render of the input.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <ImageSequence
                  images={inputs}
                  captions={inOutCaps}
                  label="Inputs"
                  ratio="square"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <ImageSequence
                  images={outputs}
                  captions={inOutCaps}
                  label="Outputs"
                  ratio="square"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Technical documentation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The bonding mechanism is an electropermanent magnet switch: a hybrid coil-permanent-magnet circuit that flips bond state with a single current pulse, then holds without power until the next pulse arrives.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/Process images/Schematic of circuit.png`}
                  alt="CTEP — circuit schematic"
                  ratio="classic"
                  fit="contain"
                  caption="Circuit schematic"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Process images/VideoCapture_20210913-213454.jpg`}
                  alt="Magnet switch — bench test still"
                  ratio="classic"
                  fit="cover"
                  caption="Magnet switch — bench test"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Development process">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Process images/20211116_170022.jpg`} alt="Prototype build, photo 1" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Process images/IMG-20211101-WA0007.jpg`} alt="Prototype build, photo 2" ratio="square" fit="cover" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Process images/IMG-20211101-WA0010.jpg`} alt="Prototype build, photo 3" ratio="square" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Testing demonstration">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/CTEP Flip.gif`}
                  alt="CTEP — assembly flip demonstration"
                  ratio="video"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <p className={blockItalicSecondaryNoteClass}>
            Full bench-test video available on request.
          </p>
        </BlockSection>
      </div>
    </main>
  );
}
